import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { useAuth } from "../../../contexts/AuthContext";
import { useConvexProjectSales, useConvexProjects } from "../../../services/convexService";
import { toast } from "sonner";
import { Id } from "../../../../backend/_generated/dataModel";
import { useLanguage } from "../../../contexts/LanguageContext";
import { api } from "../../../../backend/_generated/api";
import { useQuery } from "convex/react";

const CreateProjectSale = () => {
  const { user } = useAuth();
  const { createProjectSale } = useConvexProjectSales();
  const { updateProject } = useConvexProjects();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [formData, setFormData] = useState({
    projectId: "",
    saleAmount: "",
    saleAgreement: "",
  });

  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch projects that belong to the user (project holder) or approved projects (for admin)
  const allProjects = useQuery(api.projects.getProjectsByStatus, {
    status: "approved"
  });

  useEffect(() => {
    if (allProjects && user) {
      if (user.role === "project_holder") {
        // Filter projects to only show ones owned by the current user
        const userProjects = allProjects.filter((project: any) => project.ownerId === user.id);
        setProjects(userProjects);
      } else if (user.role === "admin") {
        setProjects(allProjects);
      } else {
        setProjects([]);
      }
      setLoading(false);
    }
  }, [allProjects, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.projectId || !formData.saleAmount) {
      toast.error("Please fill in all required fields");
      return;
    }

    const saleAmount = parseFloat(formData.saleAmount);
    if (isNaN(saleAmount) || saleAmount <= 0) {
      toast.error("Please enter a valid sale amount");
      return;
    }

    try {
      // Determine the seller ID based on user role
      const sellerId = user?.role === "admin"
        ? projects.find(p => p._id === formData.projectId)?.ownerId
        : user?.id;

      if (!sellerId) {
        toast.error("Unable to determine seller for this project");
        return;
      }

      await createProjectSale({
        projectId: formData.projectId as Id<"projects">,
        sellerId: sellerId,
        saleAmount: saleAmount,
        saleAgreement: formData.saleAgreement || undefined,
      });

      // Update the project's purpose to "sell" so it appears in project sales page
      await updateProject({
        projectId: formData.projectId as Id<"projects">,
        projectPurpose: "sell"
      });

      toast.success("Project sale listing created successfully!");
      navigate("/admin"); // Redirect to admin dashboard after creation
    } catch (error: any) {
      console.error("Error creating project sale:", error);
      toast.error(error.message || "Failed to create project sale");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== "admin" && user.role !== "project_holder")) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md w-full">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p className="text-muted-foreground mb-6">You don't have permission to create project sales.</p>
          <Button onClick={() => navigate("/")} className="w-full">Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full px-4 py-6 sm:px-6 lg:px-8" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="max-w-3xl mx-auto w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{t("projectSale.createSaleTitle") || "Create Project Sale Listing"}</h1>
          <p className="text-muted-foreground">
            {t("projectSale.createSaleDescription") || "List a project for sale to potential buyers"}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t("projectSale.saleDetails") || "Sale Details"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="projectId">
                  {t("projectSale.selectProject") || "Select Project"}
                </Label>
                <Select
                  value={formData.projectId}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, projectId: value }))}
                  required
                >
                  <SelectTrigger className={language === "ar" ? "pr-10" : "pl-10"}>
                    <SelectValue placeholder={t("projectSale.selectProjectPlaceholder") || "Choose a project to sell"} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project._id} value={project._id}>
                        {project.title} - {project.category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {t("projectSale.projectSelectionHelp") || "Select the project you want to sell"}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="saleAmount">
                  {t("projectSale.saleAmount") || "Sale Amount"}
                </Label>
                <Input
                  id="saleAmount"
                  name="saleAmount"
                  type="number"
                  placeholder="0.00"
                  value={formData.saleAmount}
                  onChange={handleInputChange}
                  required
                />
                <p className="text-sm text-muted-foreground">
                  {t("projectSale.saleAmountHelp") || "Enter the price for which you want to sell the project"}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="saleAgreement">
                  {t("projectSale.saleAgreement") || "Sale Agreement (Optional)"}
                </Label>
                <Textarea
                  id="saleAgreement"
                  name="saleAgreement"
                  placeholder={t("projectSale.saleAgreementPlaceholder") || "Enter any special terms or conditions for the sale..."}
                  value={formData.saleAgreement}
                  onChange={handleInputChange}
                  rows={4}
                />
                <p className="text-sm text-muted-foreground">
                  {t("projectSale.saleAgreementHelp") || "Add any special terms, conditions, or additional information about the sale"}
                </p>
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                >
                  {t("projectSale.createListing") || "Create Sale Listing"}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard/project-holder")}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateProjectSale;